// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, orgAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]                   = useState(null);
  const [orgs, setOrgs]                   = useState([]);
  const [activeOrg, setActiveOrg]         = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [orgsLoading, setOrgsLoading]     = useState(false);

  // ── Load orgs for any authenticated user ──────────────
  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const { data } = await orgAPI.getMine();
      const orgList = data.data.orgs || [];
      setOrgs(orgList);

      const savedOrgId = localStorage.getItem('activeOrgId');
      const saved = orgList.find((o) => o.id === savedOrgId);
      const defaultOrg = saved || orgList[0] || null;
      setActiveOrg(defaultOrg);

      if (defaultOrg?.groupProfiles?.length) {
        const savedProfileId = localStorage.getItem('activeProfileId');
        const savedProfile = defaultOrg.groupProfiles.find((p) => p.id === savedProfileId);
        setActiveProfile(savedProfile || defaultOrg.groupProfiles[0]);
      } else {
        setActiveProfile(null);
      }
    } catch {
      // User has no org memberships — that's fine
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  // ── Load current user on mount ────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoading(false);
      return;
    }
    authAPI.me()
      .then(async ({ data }) => {
        const u = data.data?.user || data.data;
        setUser(u);
        await loadOrgs();
      })
      .catch(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // ── Auth actions ──────────────────────────────────────
  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    const { tokens, user: u } = data.data;
    const { accessToken, refreshToken } = tokens;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setUser(u);
    await loadOrgs();
    return u;
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) await authAPI.logout({ refreshToken });
    } catch {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('activeOrgId');
    localStorage.removeItem('activeProfileId');
    setUser(null);
    setOrgs([]);
    setActiveOrg(null);
    setActiveProfile(null);
  };

  // ── Org / Profile switchers ───────────────────────────
  const switchOrg = useCallback((org) => {
    setActiveOrg(org);
    localStorage.setItem('activeOrgId', org.id);
    const firstProfile = org.groupProfiles?.[0] || null;
    setActiveProfile(firstProfile);
    if (firstProfile) localStorage.setItem('activeProfileId', firstProfile.id);
    else localStorage.removeItem('activeProfileId');
  }, []);

  const switchProfile = useCallback((profile) => {
    setActiveProfile(profile);
    localStorage.setItem('activeProfileId', profile.id);
  }, []);

  const refreshOrgs = useCallback(async () => {
    await loadOrgs();
  }, [loadOrgs]);

  // ── Derived role flags ────────────────────────────────
  // Org-level roles (from getMyOrgs → myOrgRole)
  const isOrgOwner   = activeOrg?.myOrgRole === 'OWNER';
  const isOrgBilling = activeOrg?.myOrgRole === 'BILLING';

  // Profile-level roles (from profile.myProfileRole on the active profile)
  // Org OWNERs have implicit ADMIN on all profiles
  const _profileRole   = activeProfile?.myProfileRole;
  const isProfileAdmin  = isOrgOwner || _profileRole === 'ADMIN';
  const isProfileMember = isOrgOwner || _profileRole === 'ADMIN' || _profileRole === 'MEMBER';

  // Convenience aliases used widely across the app:
  // isAdmin  → can edit profiles (org owner or profile admin)
  // isMember → has any profile access
  const isAdmin  = isProfileAdmin;
  const isMember = isProfileMember;

  return (
    <AuthContext.Provider value={{
      user, loading, orgsLoading,
      orgs, activeOrg, activeProfile,
      login, logout,
      switchOrg, switchProfile, refreshOrgs,
      hasOrgAccess: orgs.length > 0,
      // Org-level
      isOrgOwner,
      isOrgBilling,
      // Profile-level
      isProfileAdmin,
      isProfileMember,
      // Aliases (backward-compatible names)
      isOwner: isOrgOwner,
      isAdmin,
      isMember,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
